<template>
  <div class="plm-root">
    <div class="plm-header">
      <h1>Project Output Localisation Map</h1>
    </div>
    <div class="plm-body">
      <div class="plm-left" ref="leftPane">
        <div class="plm-picker">
          <label class="plm-label">Project Output</label>
          <DxSelectBox
            :items="outputItems"
            display-expr="Title"
            value-expr="TopicId"
            :show-clear-button="true"
            :search-enabled="true"
            :search-mode="'contains'"
            v-model:value="selectedTopicId"
            placeholder="Select a Project Output"
          />
        </div>
        <div class="plm-meta" v-if="selectedTopic">
          <div class="plm-meta-title">{{ selectedTopic.Title }}</div>
          <div class="plm-meta-desc" v-if="selectedTopic.TpcDescription">{{ selectedTopic.TpcDescription }}</div>
        </div>
        <div class="plm-hint" v-if="!selectedTopicId">Pick one Project Output to visualize its spatial coverage.</div>
        <div class="plm-loading" v-if="isLoading">
          <DxLoadIndicator :visible="true" :height="20" :width="20" />
          <span>Loading coverage…</span>
        </div>
        <div class="plm-hint warn" v-if="!isLoading && selectedTopicId && (!hasAnySelection)">
          No spatial coverage found for this item.
        </div>
        <div
          class="plm-country-list"
          v-if="selectedCountryNames && selectedCountryNames.length"
          ref="countryList"
          :style="countryListStyle"
        >
          <div class="plm-country-list-title">Countries ({{ selectedCountryNames.length }})</div>
          <ul class="plm-country-ul">
            <li v-for="n in selectedCountryNames" :key="n" @click="focusCountry(n)" class="clickable-item">{{ n }}</li>
          </ul>
        </div>
      </div>
      <div class="plm-right">
        <MapLibreCoverageMap
          ref="coverageMap"
          :selected-countries-dict="selectedCountriesDict"
          :aspect-ratio="2"
          :bottom-safe-margin="16"
          @resized="onMapResized"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import DxSelectBox from 'devextreme-vue/select-box';
import DxLoadIndicator from 'devextreme-vue/load-indicator';
import MapLibreCoverageMap from './widgets/MapLibreCoverageMap.vue';

export default {
  name: 'ProjectLocalisationMap',
  components: { DxSelectBox, MapLibreCoverageMap, DxLoadIndicator },
  data() {
    return {
      outputsLoaded: false,
      outputItems: [],
      selectedTopicId: null,
      selectedCountriesDict: { iso3: [], names: [] },
      isLoading: false,
      resolvedArgsByTopic: {},
      // layout helpers
      mapHeightPx: 0,
      countryListMaxPx: 0,
      bottomSafeMarginPx: 32,
    };
  },
  computed: {
    countryMapNameIso3() { return this.$store.getters.countryMapNameIso3; },
    countryMapIso3Name() { return this.$store.getters.countryMapIso3Name; },
    selectedTopic() {
      if (!this.selectedTopicId) return null;
      return (this.outputItems || []).find((t) => String(t.TopicId) === String(this.selectedTopicId)) || null;
    },
    selectedCountryNames() {
      const names = Array.isArray(this.selectedCountriesDict?.names) ? this.selectedCountriesDict.names : [];
      return [...new Set(names)].sort((a, b) => String(a).localeCompare(String(b)));
    },
    hasAnySelection() {
      const d = this.selectedCountriesDict || {};
      const n = Array.isArray(d.names) ? d.names.length : 0;
      const i3 = Array.isArray(d.iso3) ? d.iso3.length : 0;
      return (n + i3) > 0;
    },
    countryListStyle() {
      const maxH = this.countryListMaxPx > 0 ? `${Math.max(120, Math.round(this.countryListMaxPx))}px` : 'auto';
      return { maxHeight: maxH, overflowY: 'auto' };
    },
  },
  mounted() {
    try { document.title = 'Project Output Localisation Map'; } catch {/* ignore */}
    // Preload ISO3 mapping from CountryService (no DB schema change; existing endpoint)
    this.loadCountryList();
    this.loadOutputs();
    window.addEventListener('resize', this.recalcCountryListMax, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.recalcCountryListMax);
  },
  methods: {
    loadCountryList() { return this.$store.dispatch('loadCountryList'); },
    _canonName(s) {
      try {
        return String(s || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .trim();
      } catch { return String(s || '').toLowerCase().trim(); }
    },
    async loadOutputs() {
      const filter = {
        ttyId: 7,
        rouId: 0,
        stgId: 0,
        excludeStgId: 0,
        statusId: 0,
        onlyAssignedTopics: false,
        onlyOwnedTopics: false,
        showBookmarkFilter: false,
        showRoundFilter: false,
        showExpertsModeSwitch: false,
        showOwnerStatus: false,
        showStatusFilter: false,
        showAddNew: false,
        useFullTextSearch: false,
        onlyPublishedInFilter: false,
        filterTtyId: 0,
        filterOnlyMy: false,
        filterTltId: 0,
        filterStgId: 15,
        filterTitle: 'Project Outputs',
        columns: ['TopicId', 'Title', 'TpcDescription'],
        columnsConfig: [],
        hiddenColumns: [],
      };
      const ok = await this.$store.dispatch('getTopicList', filter);
      if (ok === true) {
        const list = this.$store.state.topicStore?.topicList || [];
        this.outputItems = (list || []).map((it) => ({
          ...it,
          Title: it.Title || it.title || it.TpcTitle || `#${it.TopicId}`,
        }));
      }
    },
    async loadCoverageForSelected() {
      this.selectedCountriesDict = { iso3: [], names: [] };
      if (!this.selectedTopicId) { this.isLoading = false; return; }
      this.isLoading = true;
      try {
        const ensuredArgs = await this.ensureArgsForTopic(this.selectedTopicId);
        const elements = await this.fetchGenericFormElements(this.selectedTopicId, ensuredArgs);
        if (!Array.isArray(elements) || elements.length === 0) { return; }

        const candidates = elements.filter((e) => {
          const isList = [3, 12, 14, 20, 21, 24].includes(e.AtyId);
          const name = (e.Name || '').toLowerCase();
          const cap = (e.Caption || '').toLowerCase();
          const looksLikeCoverage = /coverage|spatial|country|countries|location/.test(name + ' ' + cap);
          return isList && (looksLikeCoverage || Array.isArray(e.SelectBoxItems));
        });

        let best = null;
        let bestScore = -1;
        candidates.forEach((fld) => {
          const texts = this.getSelectedItemTexts(fld);
          const score = this.scoreCountryLikelihood(fld, texts);
          if (score > bestScore) { best = fld; bestScore = score; }
        });

        if (!best) { return; }

        // Expand group selections (continents) to countries using ParentValue relations
        const flatItems = this._flattenItems(Array.isArray(best.SelectBoxItems) ? best.SelectBoxItems : []);
        const selectedItems = this.getSelectedItems(best);
        const expanded = this._expandGroupSelections(selectedItems, flatItems);

        // Build structured dictionary for the map: include all names even if codes are missing
        const dict = { iso3: [], names: [] };
        const setIso3 = new Set();
        const setNames = new Set();
        expanded.forEach((it) => {
          const txt = String(it?.Text || '').trim();
          if (!txt) return;
          const raw = txt;
          const baseName = raw.replace(/\s*\([^)]*\)\s*$/, '').trim();
          if (baseName) setNames.add(baseName);
          // explicit code in parentheses
          const m = raw.match(/\(([^)]+)\)\s*$/);
          if (m && m[1]) {
            const code = String(m[1]).trim().toUpperCase();
            if (code !== 'NULL' && code !== '-99') {
              if (/^[A-Z]{3}$/.test(code)) setIso3.add(code);
            }
          }
          // DB mapping by name
          const canon = this._canonName(baseName);
          const iso3FromDb = this.countryMapNameIso3?.[canon];
          if (iso3FromDb) setIso3.add(String(iso3FromDb).toUpperCase());
        });
        dict.iso3 = Array.from(setIso3);
        dict.names = Array.from(setNames);
        this.selectedCountriesDict = dict;
        this.$nextTick(() => this.recalcCountryListMax());
      } finally {
        this.isLoading = false;
      }
    },
    // Return selected items (objects) from a SelectBox field
    getSelectedItems(field) {
      if (!field) return [];
      const keys = Array.isArray(field.SelectedKeys)
        ? field.SelectedKeys
        : (typeof field.Value === 'string' && field.Value.length > 0
            ? field.Value.split(',').map((s) => s.trim()).filter(Boolean)
            : (field.Value != null ? [field.Value] : []));
      const flat = this._flattenItems(Array.isArray(field.SelectBoxItems) ? field.SelectBoxItems : []);
      const byVal = new Map(flat.map((it) => [String(it.Value), it]));
      const out = [];
      keys.forEach((k) => { const hit = byVal.get(String(k)); if (hit) out.push(hit); });
      return out;
    },
    // Flatten nested SelectBoxItems into a single-level array
    _flattenItems(items) {
      const out = [];
      const walk = (arr) => {
        for (const it of (arr || [])) {
          if (!it || typeof it !== 'object') continue;
          out.push({ Text: it.Text, Value: it.Value, ParentValue: it.ParentValue, Items: Array.isArray(it.Items) ? it.Items : [] });
          if (it.Items && it.Items.length) walk(it.Items);
        }
      };
      walk(items);
      return out;
    },
    // Expand groups recursively: walk ParentValue relationships until leaf items (countries) are reached
    _expandGroupSelections(selectedItems, flatItems) {
      const byParent = new Map();
      for (const it of flatItems) {
        const p = String(it.ParentValue ?? '');
        if (!byParent.has(p)) byParent.set(p, []);
        byParent.get(p).push(it);
      }
      const visited = new Set();
      const leaves = new Map(); // Value -> item (dedup)
      const stack = [...(selectedItems || [])];

      while (stack.length) {
        const node = stack.pop();
        const key = String(node?.Value);
        if (!key || visited.has(key)) continue;
        visited.add(key);

        const children = byParent.get(key) || [];
        if (children.length > 0) {
          // Push children and continue
          for (const ch of children) stack.push(ch);
        } else {
          // No children: treat as leaf (country)
          leaves.set(key, node);
        }
      }

      return Array.from(leaves.values());
    },
    async fetchGenericFormElements(topicId, argsToken) {
      const applicationServiceUri = 'WebApi.asmx/api/applicationService';
      const fd = new FormData();
      fd.append('topicId', topicId);
      if (argsToken) fd.append('args', argsToken);
      try {
        const res = await axios.post(`${applicationServiceUri}/GetGenericFormData`, fd);
        if (res?.data?.success) return res.data.formElements || [];
        return [];
      } catch { return []; }
    },
    async ensureArgsForTopic(topicId) {
      if (this.resolvedArgsByTopic && this.resolvedArgsByTopic[topicId]) return this.resolvedArgsByTopic[topicId];
      try {
        const topicServiceUri = 'WebApi.asmx/api/topicService';
        const res = await axios.get(`${topicServiceUri}/GetTopicArgs`, { params: { topicId } });
        const token = res?.data?.args || '';
        if (token) {
          this.$set ? this.$set(this.resolvedArgsByTopic, topicId, token) : (this.resolvedArgsByTopic[topicId] = token);
          this.persistArgsInUrl(token);
          return token;
        }
      } catch { /* ignore */ }
      return '';
    },
    persistArgsInUrl(token) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('args', token);
        window.history.replaceState({}, '', url.toString());
      } catch {/* ignore */}
    },
    getSelectedItemTexts(field) {
      if (!field) return [];
      const keys = Array.isArray(field.SelectedKeys)
        ? field.SelectedKeys
        : (typeof field.Value === 'string' && field.Value.length > 0
            ? field.Value.split(',').map((s) => s.trim()).filter(Boolean)
            : (field.Value != null ? [field.Value] : []));
      const items = Array.isArray(field.SelectBoxItems) ? field.SelectBoxItems : [];
      const findTextByValue = (arr, val) => {
        for (const it of arr) {
          if (String(it.Value) === String(val)) return it.Text;
          if (Array.isArray(it.Items) && it.Items.length) {
            const found = findTextByValue(it.Items, val);
            if (found) return found;
          }
        }
        return null;
      };
      const texts = [];
      keys.forEach((k) => {
        const t = findTextByValue(items, k);
        if (t) texts.push(String(t));
      });
      return texts;
    },
    scoreCountryLikelihood(field, texts) {
      // Composite score combining:
      // - field hints (name/caption contains coverage keywords)
      // - how many of the field's items look like countries per CountryService mapping
      // - selected texts that look like ISO3 or resolvable country names
      let score = 0;
      try {
        const name = String(field?.Name || '').toLowerCase();
        const cap = String(field?.Caption || '').toLowerCase();
        if (/\b(spatial|coverage|country|countries|location|global)\b/.test(name + ' ' + cap)) {
          score += 5; // strong hint
        }
        // Penalize obvious non-coverage pickers
        if (/final product type|output type|access level|download options|user interface|data release|sef theme|applied sector/i.test(cap)) {
          score -= 3;
        }
        // Count how many item texts look like country names
        const items = Array.isArray(field?.SelectBoxItems) ? field.SelectBoxItems : [];
        const countryish = this._countCountryLikeInItems(items);
        score += Math.min(50, countryish); // each country-like item adds +1 up to a cap
        // Selected texts evidence
        (texts || []).forEach((txt) => {
          const a = String(txt || '').trim();
          if (/\(([A-Z]{3})\)\s*$/.test(a)) score += 2; // explicit ISO3 in parentheses
          else if (/^[A-Z]{3}$/.test(a)) score += 2; // raw ISO3 token
          else {
            const base = a.replace(/\s*\([^)]*\)\s*$/, '').trim();
            const canon = this._canonName(base);
            if (this.countryMapNameIso3 && this.countryMapNameIso3[canon]) score += 1; // resolvable country name
          }
        });
      } catch { /* ignore */ }
      return score;
    },
    _countCountryLikeInItems(items) {
      try {
        if (!Array.isArray(items) || !items.length) return 0;
        let cnt = 0;
        for (const it of items) {
          const t = String(it?.Text || '').trim();
          const canon = this._canonName(t);
          if (canon && this.countryMapNameIso3 && this.countryMapNameIso3[canon]) cnt += 1;
          if (Array.isArray(it?.Items) && it.Items.length) cnt += this._countCountryLikeInItems(it.Items);
        }
        return cnt;
      } catch { return 0; }
    },
    recalcCountryListMax() {
      this.$nextTick(() => {
        const el = this.$refs.countryList;
        if (!el) return;
        try {
          const rect = el.getBoundingClientRect();
          const vp = window.innerHeight || document.documentElement.clientHeight || 800;
          const available = vp - rect.top - this.bottomSafeMarginPx;
          this.countryListMaxPx = Math.max(120, Math.floor(available));
        } catch { /* ignore */ }
      });
    },
    onMapResized(h) {
      this.mapHeightPx = h || 0;
      this.recalcCountryListMax();
    },
    focusCountry(name) {
      if (this.$refs.coverageMap && typeof this.$refs.coverageMap.flyToCountry === 'function') {
        this.$refs.coverageMap.flyToCountry(name);
      }
    },
  },
  watch: {
    selectedTopicId() {
      this.loadCoverageForSelected();
    },
    selectedCountriesDict: {
      deep: true,
      handler() { this.$nextTick(() => this.recalcCountryListMax()); },
    },
  },
};
</script>

<style scoped>
.plm-root { width: 100%; }
.plm-header { margin: 10px 0 20px; }
.plm-body { display: grid; grid-template-columns: 360px 1fr; gap: 24px; }
.plm-left { display: grid; gap: 16px; align-content: start; }
.plm-right { min-width: 0; }
/* Ensure right pane doesn't create page scrolls; Map component will cap height to viewport */
.plm-right { display: flex; flex-direction: column; min-height: 0; overflow: hidden; }
.plm-right > * { min-height: 0; }
.plm-label { display: block; margin-bottom: 8px; font-weight: 600; }
.plm-meta-title { font-weight: 600; }
.plm-meta-desc { color: #666; font-size: 13px; white-space: pre-line; }
.plm-hint { color: #666; font-size: 13px; }
.plm-hint.warn { color: #a94442; }
.plm-country-list { margin-top: 12px; }
.plm-country-list-title { font-weight: 600; margin-bottom: 6px; }
.plm-country-ul { list-style: none; margin: 0; padding: 0; columns: 2; column-gap: 16px; }
.plm-country-ul li { break-inside: avoid; font-size: 13px; line-height: 1.6; }
.plm-country-ul li.clickable-item { cursor: pointer; transition: color 0.2s; }
.plm-country-ul li.clickable-item:hover { color: #1e90ff; text-decoration: underline; }
.plm-loading { display: inline-flex; align-items: center; gap: 8px; color: #666; font-size: 13px; }
/* map engine toggle removed */
</style>
